/**
 * Recurring bookings (P2, #642): the request a manager's series form
 * builds, and how a series and its dry run read back.
 *
 * A series is staff-only — a league night, a standing Tuesday court —
 * and books one ordinary reservation per occurrence through the normal
 * booking path, each checked against the amenity's rules. An occurrence
 * that breaks a rule is **skipped**, not forced; the dry run shows which
 * and why before anything is booked, and overrides apply to every
 * occurrence (per rule code, with one reason), as on a single booking.
 *
 * The rule mirrors the server's (`reservation/series.rs`): weekly or
 * every other week on the chosen weekdays, or monthly on the nth (1–4) or
 * last (-1) weekday; it stops on a date or after a count, never more than
 * 52 occurrences.
 *
 * Pure, so it is unit-tested.
 */
import {
  ReservationRuleCode,
  ReservationSeriesCreate,
  ReservationSeriesFrequency,
  ReservationSeriesCancelPreviewReturn,
  ReservationSeriesResult,
  ReservationSeriesReturn,
} from '../clients/rust-api-client';
import {
  WEEKDAYS,
  isZeroMoney,
  formatDayLabel,
  formatDuration,
  formatMinute,
  titleCase,
} from './reservationMeta';

export const MAX_OCCURRENCES = 52;

export const FREQUENCY_LABELS: Record<ReservationSeriesFrequency, string> = {
  weekly: 'Every week',
  biweekly: 'Every other week',
  monthly_nth_weekday: 'Monthly, on a weekday',
};

export const MONTH_WEEK_LABELS: Record<number, string> = {
  1: 'First',
  2: 'Second',
  3: 'Third',
  4: 'Fourth',
  [-1]: 'Last',
};

export interface SeriesForm {
  amenityId: string | null;
  homeId: string | null;
  frequency: ReservationSeriesFrequency;
  weekdays: number[];
  /** Monthly only: 1–4, or -1 for the last. */
  monthWeek: number;
  startMinute: number | null;
  durationMinutes: number | null;
  startsOn: string;
  /** How it stops: on a date, or after a number of occurrences. */
  endsBy: 'date' | 'count';
  untilOn: string;
  count: string;
  partySize: string;
  purpose: string;
  internalNotes: string;
}

/** Why the form cannot be sent yet, or null when it can. */
export const seriesFormProblem = (f: SeriesForm): string | null => {
  if (!f.homeId) return 'Choose the household.';
  if (!f.amenityId) return 'Choose the amenity.';
  if (f.weekdays.length === 0) return 'Choose a weekday.';
  if (f.startMinute === null) return 'Choose a start time.';
  if (!f.durationMinutes) return 'Choose a length.';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f.startsOn)) return 'Choose the first day.';
  if (f.endsBy === 'date') {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(f.untilOn)) return 'Choose the last day.';
    if (f.untilOn < f.startsOn) return 'The last day is before the first.';
  } else {
    const n = Number(f.count);
    if (!/^\d+$/.test(f.count) || n < 1 || n > MAX_OCCURRENCES)
      return `Between 1 and ${MAX_OCCURRENCES} occurrences.`;
  }
  if (!/^\d+$/.test(f.partySize) || Number(f.partySize) < 1) return 'Party size must be at least one.';
  return null;
};

/**
 * The create body. `dryRun` asks the server what would be booked and
 * skipped without booking anything. Monthly series use the first chosen
 * weekday only, as the server does.
 */
export const buildSeriesRequest = (
  f: SeriesForm,
  opts: { dryRun: boolean; overrides?: string[]; overrideReason?: string }
): ReservationSeriesCreate | null => {
  if (seriesFormProblem(f) !== null) return null;
  const monthly = f.frequency === 'monthly_nth_weekday';
  const overrides = opts.overrides?.length ? (opts.overrides as ReservationRuleCode[]) : null;
  return {
    amenity_id: f.amenityId!,
    home_id: f.homeId!,
    frequency: f.frequency,
    weekdays: monthly ? [f.weekdays[0]] : [...f.weekdays].sort((a, b) => a - b),
    month_week: monthly ? f.monthWeek : null,
    start_minute: f.startMinute!,
    duration_minutes: f.durationMinutes!,
    starts_on: f.startsOn,
    until_on: f.endsBy === 'date' ? f.untilOn : null,
    occurrence_count: f.endsBy === 'count' ? Number(f.count) : null,
    party_size: Number(f.partySize),
    purpose: f.purpose.trim() || null,
    internal_notes: f.internalNotes.trim() || null,
    dry_run: opts.dryRun,
    overrides,
    override_reason: overrides ? opts.overrideReason?.trim() || null : null,
  };
};

export interface SeriesPreviewSummary {
  wouldBook: number;
  wouldSkip: number;
  /** Codes that skip some occurrence and a manager may override. */
  overridable: string[];
  /** Codes that skip some occurrence and nothing can override. */
  hard: string[];
}

/** The dry run, totalled: what books, what is skipped, and which rules do the skipping. */
export const summarizeSeriesResult = (r: ReservationSeriesResult): SeriesPreviewSummary => {
  const overridable = new Set<string>();
  const hard = new Set<string>();
  for (const o of r.occurrences) {
    for (const v of o.violations) (v.overridable ? overridable : hard).add(v.code);
  }
  return {
    wouldBook: r.occurrences.filter((o) => o.booked || o.violations.length === 0).length,
    wouldSkip: r.occurrences.filter((o) => !o.booked && o.violations.length > 0).length,
    overridable: Array.from(overridable).sort(),
    hard: Array.from(hard).sort(),
  };
};

const joinWords = (words: string[]): string =>
  words.length <= 1
    ? words.join('')
    : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;

/** 'Every week on Tuesday and Thursday, 6 PM for 2 hr · Oct 6 – Dec 15'. */
export const describeSeries = (
  s: Pick<
    ReservationSeriesReturn,
    | 'frequency'
    | 'weekdays'
    | 'month_week'
    | 'start_minute'
    | 'duration_minutes'
    | 'starts_on'
    | 'until_on'
    | 'occurrence_count'
  >
): string => {
  const days = joinWords([...s.weekdays].sort((a, b) => a - b).map((d) => WEEKDAYS[d] ?? String(d)));
  const when =
    s.frequency === 'monthly_nth_weekday'
      ? `Monthly on the ${(MONTH_WEEK_LABELS[s.month_week ?? 1] ?? '').toLowerCase()} ${WEEKDAYS[s.weekdays[0]] ?? ''}`
      : `${FREQUENCY_LABELS[s.frequency] ?? titleCase(s.frequency)} on ${days}`;
  const time = `${formatMinute(s.start_minute)} for ${formatDuration(s.duration_minutes)}`;
  const span = s.until_on
    ? `${formatDayLabel(s.starts_on)} – ${formatDayLabel(s.until_on)}`
    : `from ${formatDayLabel(s.starts_on)}, ${s.occurrence_count ?? MAX_OCCURRENCES} times`;
  return `${when}, ${time} · ${span}`;
};

/**
 * The money lines of a series-cancel preview — "$300.00 reversed",
 * "$75.00 kept", "$150.00 returned" — leaving out the zero ones. Empty
 * means cancelling moves no money.
 */
export const seriesCancelTotals = (
  p: Pick<ReservationSeriesCancelPreviewReturn, 'total_reversed' | 'total_kept' | 'total_returned'>,
  money: (v: string) => string
): string[] =>
  [
    [p.total_reversed, 'reversed'],
    [p.total_kept, 'kept'],
    [p.total_returned, 'returned'],
  ]
    .filter(([amount]) => !isZeroMoney(amount))
    .map(([amount, what]) => `${money(amount)} ${what}`);
